/**
 * Pure helpers for canvas copy/cut/paste/duplicate of section hierarchies.
 * Keeps parent-relative child positions intact and expands section selections
 * to include descendants.
 */

import type { Node } from "@xyflow/react";

import { getDescendantIds } from "./sectionMembership";

export function isCopiableNode(node: Node): boolean {
  return (
    node.type !== "chatSession" &&
    !(node.data as { loading?: boolean } | undefined)?.loading
  );
}

/**
 * Absolute flow position by walking `parentId` through `byId`.
 * Stops when a parent is missing from the map.
 */
export function absolutePositionInMap(
  node: Node,
  byId: Map<string, Node>,
): { x: number; y: number } {
  let x = node.position.x;
  let y = node.position.y;
  let cur: Node | undefined = node;
  while (cur?.parentId) {
    const parent = byId.get(cur.parentId);
    if (!parent) break;
    x += parent.position.x;
    y += parent.position.y;
    cur = parent;
  }
  return { x, y };
}

/**
 * When a section is among the candidates, include all of its descendants
 * (nested sections included) from `allNodes`. Deduplicates and skips
 * non-copiable nodes.
 */
export function expandWithSectionDescendants(
  candidates: Node[],
  allNodes: Node[],
): Node[] {
  const byId = new Map(allNodes.map((n) => [n.id, n]));
  const seen = new Set<string>();
  const result: Node[] = [];

  const add = (n: Node) => {
    if (seen.has(n.id) || !isCopiableNode(n)) return;
    seen.add(n.id);
    result.push(n);
  };

  for (const n of candidates) {
    add(n);
    if (n.type !== "section") continue;
    for (const id of getDescendantIds(n.id, allNodes)) {
      const child = byId.get(id);
      if (child) add(child);
    }
  }

  return result;
}

/**
 * For nodes whose parent is not in the clipboard set, convert parent-relative
 * positions to canvas-absolute and clear `parentId` so paste treats them as roots.
 */
export function materializeClipboardNodes(
  nodes: Node[],
  allNodes: Node[],
): Node[] {
  const copiedIds = new Set(nodes.map((n) => n.id));
  const allById = new Map(allNodes.map((n) => [n.id, n]));

  return nodes.map((n) => {
    if (!n.parentId || copiedIds.has(n.parentId)) return n;
    return {
      ...n,
      position: absolutePositionInMap(n, allById),
      parentId: undefined,
    };
  });
}

/** True when the node has no parent in the pasted/copied set. */
export function isPasteForestRoot(
  node: Node,
  pastedIds: ReadonlySet<string>,
): boolean {
  return !node.parentId || !pastedIds.has(node.parentId);
}

/**
 * Compute paste positions: offset only forest roots so the group's absolute
 * top-left lands at `pastePos`. Children whose parent is also pasted keep
 * their parent-relative coordinates unchanged.
 */
export function rebasePastePositions(
  nodes: Node[],
  pastePos: { x: number; y: number },
  snap: (v: number) => number = (v) => v,
): Map<string, { x: number; y: number }> {
  const pastedIds = new Set(nodes.map((n) => n.id));
  const roots = nodes.filter((n) => isPasteForestRoot(n, pastedIds));

  const result = new Map<string, { x: number; y: number }>();
  if (roots.length === 0) {
    for (const n of nodes) {
      result.set(n.id, { x: n.position.x, y: n.position.y });
    }
    return result;
  }

  const minX = Math.min(...roots.map((n) => n.position.x));
  const minY = Math.min(...roots.map((n) => n.position.y));

  for (const n of nodes) {
    if (isPasteForestRoot(n, pastedIds)) {
      result.set(n.id, {
        x: snap(pastePos.x + (n.position.x - minX)),
        y: snap(pastePos.y + (n.position.y - minY)),
      });
    } else {
      result.set(n.id, { x: n.position.x, y: n.position.y });
    }
  }

  return result;
}

/**
 * Top-left of the forest roots in the clipboard (for duplicate offset).
 * Uses absolute positions walked within the node set.
 */
export function clipboardRootMinPosition(nodes: Node[]): {
  x: number;
  y: number;
} {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const pastedIds = new Set(nodes.map((n) => n.id));
  const roots = nodes.filter((n) => isPasteForestRoot(n, pastedIds));
  if (roots.length === 0) {
    return { x: 0, y: 0 };
  }
  const abs = roots.map((n) => absolutePositionInMap(n, byId));
  return {
    x: Math.min(...abs.map((p) => p.x)),
    y: Math.min(...abs.map((p) => p.y)),
  };
}

/**
 * Parent-before-child order so React Flow can resolve positions when nodes
 * are added one at a time.
 */
export function sortParentFirst(nodes: Node[]): Node[] {
  if (nodes.length < 2) return nodes;

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const ordered: Node[] = [];
  const visited = new Set<string>();

  const visit = (node: Node) => {
    if (visited.has(node.id)) return;
    visited.add(node.id);
    if (node.parentId) {
      const parent = byId.get(node.parentId);
      if (parent) visit(parent);
    }
    ordered.push(node);
  };

  for (const node of nodes) visit(node);
  return ordered;
}

/**
 * Expand section descendants, materialize orphan positions, ready for clipboard
 * or immediate paste (duplicate).
 */
export function prepareClipboardNodes(
  candidates: Node[],
  allNodes: Node[],
): Node[] {
  return materializeClipboardNodes(
    expandWithSectionDescendants(candidates, allNodes),
    allNodes,
  );
}
